import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { Camera, X, Loader2, Info } from "lucide-react";

// Scanner-safety patches are installed by side-effect import in
// /app/frontend/src/index.js — see /app/frontend/src/lib/scannerSafety.js.
// Anything that trickles through despite those patches (rare race
// conditions on some Android WebViews) shows up here only as a soft
// warning banner. It never blocks scanning or product lookup.
const IGNORED_SCANNER_ERRORS = ["setPhotoOptions", "OverconstrainedError"];
const isIgnorableScannerError = (msg) => {
    if (!msg) return false;
    const s = String(msg);
    return IGNORED_SCANNER_ERRORS.some((k) => s.includes(k));
};

// ZXing decode tuning:
//  - Restrict to grocery/retail barcode symbologies (EAN/UPC family + Code128
//    used by some Finnish suppliers). Running all 15+ formats on every frame
//    slows detection to ~1-2 seconds on mid-range phones.
//  - TRY_HARDER runs a heavier decode pattern (rotations + more aggressive
//    binarizer) which massively improves reads on cans and curved packaging.
const GROCERY_FORMATS = [
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.ITF,
];
const buildDecodeHints = () => {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, GROCERY_FORMATS);
    hints.set(DecodeHintType.TRY_HARDER, true);
    return hints;
};

/**
 * BarcodeScanner — camera-based barcode reader with manual fallback.
 *
 * Why this component exists (Prompt 19):
 *   USB and Bluetooth scanners already work via the inventory `scan` input
 *   (keyboard-wedge: they emit keystrokes + Enter). What was missing was the
 *   phone-camera path. This modal owns that flow.
 *
 * Behaviour:
 *   - Opens a modal with a live <video> preview tied to the rear camera
 *     (`facingMode: 'environment'`).
 *   - On the first detected barcode, calls `onScan(code)` and closes.
 *   - If `getUserMedia` is unavailable, denied, or fails for any reason,
 *     falls back to a manual text input so the workflow is never blocked
 *     (Prompt 19: "Every workflow should have a manual fallback").
 *
 * Architecture:
 *   The lookup itself is owned by the parent (Inventory.jsx) which already
 *   calls `GET /api/products/lookup?barcode=…`. This component is purely a
 *   capture device. That mirrors the `BarcodeProvider` extension point
 *   defined in `/app/backend/automation/interfaces.py` — the camera is one
 *   provider; future external-database providers slot in the same way.
 */
export default function BarcodeScanner({ open, onClose, onScan }) {
    const { t } = useTranslation();
    const videoRef = useRef(null);
    const readerRef = useRef(null);
    const controlsRef = useRef(null);
    const streamRef = useRef(null);
    // Decoder-level re-entry guard. Applied strictly AFTER a successful
    // detection — never touches ZXing hints, formats, camera params or
    // decode timing. First valid barcode wins; any subsequent frames
    // that arrive in the tiny window between `controls.stop()` being
    // scheduled and the reader actually halting are silently dropped.
    const lockedRef = useRef(false);
    const [status, setStatus] = useState("idle"); // idle | starting | scanning | paused | error | manual
    const [errorMsg, setErrorMsg] = useState("");
    const [manualValue, setManualValue] = useState("");
    const [warnNote, setWarnNote] = useState("");

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        let attachedVideo = null;
        lockedRef.current = false;

        const start = async () => {
            setStatus("starting");
            setErrorMsg("");
            setWarnNote("");
            try {
                if (!navigator.mediaDevices?.getUserMedia) {
                    throw new Error(t("scanner.cameraApiUnavailableOnThis", "Camera API unavailable on this device"));
                }
                // Explicitly request the rear camera. Passing undefined to
                // decodeFromVideoDevice picks the FIRST camera the browser
                // enumerates which is often the front-facing one on phones,
                // making scanning unusable for kitchen staff.
                let stream = null;
                try {
                    stream = await navigator.mediaDevices.getUserMedia({
                        video: {
                            facingMode: { ideal: "environment" },
                            width: { ideal: 1280 },
                            height: { ideal: 720 },
                        },
                        audio: false,
                    });
                } catch (mediaErr) {
                    // Fallback to any camera if the rear-camera hint fails.
                    if (isIgnorableScannerError(mediaErr?.message
                                                  || mediaErr?.name)) {
                        setWarnNote(t("scanner.cameraOptionNotSupportedScanning", "Camera option not supported. Scanning will continue with default settings."));
                    }
                    stream = await navigator.mediaDevices.getUserMedia({
                        video: true, audio: false,
                    });
                }
                if (cancelled) {
                    stream.getTracks().forEach((t) => { try { t.stop(); } catch (_e) { /* ignore */ } });
                    return;
                }
                if (videoRef.current) {
                    attachedVideo = videoRef.current;
                    videoRef.current.srcObject = stream;
                    try { await videoRef.current.play(); } catch (_e) { /* ignore */ }
                }
                streamRef.current = stream;

                const reader = new BrowserMultiFormatReader(buildDecodeHints());
                readerRef.current = reader;
                const controls = await reader.decodeFromVideoElement(
                    videoRef.current,
                    (result, err) => {
                        if (cancelled) return;
                        if (result) {
                            // Decoder-level lock: accept the first valid
                            // detection ONLY. Any later frames that slip
                            // through before the reader has fully halted
                            // are dropped. Detection itself is unchanged.
                            if (lockedRef.current) return;
                            lockedRef.current = true;
                            const text = result.getText();
                            // Pause the stream immediately after the first hit so we
                            // don't keep firing while the suggestion dialog is open.
                            try { controls?.stop(); } catch (_e) { /* ignore */ }
                            setStatus("paused");
                            onScan?.(text);
                            return;
                        }
                        // err is a NotFoundException on every empty frame; that is
                        // the library's normal "no code in view" signal — ignore.
                        if (err && err.name && err.name !== "NotFoundException") {
                            if (isIgnorableScannerError(err.message || err.name)) {
                                setWarnNote(t("scanner.cameraOptionNotSupportedScanning", "Camera option not supported. Scanning will continue with default settings."));
                                return;
                            }
                            // surface unexpected errors but keep the stream alive
                            console.warn("Scanner frame error:", err.name);
                        }
                    },
                );
                controlsRef.current = controls;
                if (!cancelled) setStatus("scanning");
            } catch (e) {
                if (cancelled) return;
                if (isIgnorableScannerError(e?.message)) {
                    // Should never reach us thanks to the global handlers, but
                    // stay defensive: keep the scanner open with a soft note.
                    setWarnNote(t("scanner.cameraOptionNotSupportedScanning", "Camera option not supported. Scanning will continue with default settings."));
                    setStatus("scanning");
                    return;
                }
                setErrorMsg(e?.message || t("scanner.cameraUnavailable", "Camera unavailable"));
                setStatus("manual");
            }
        };

        start();

        return () => {
            cancelled = true;
            lockedRef.current = false;
            try { controlsRef.current?.stop(); } catch (_e) { /* ignore */ }
            controlsRef.current = null;
            readerRef.current = null;
            // Explicit teardown of the MediaStream so the camera indicator
            // actually turns off on mobile Chrome / Safari when the modal
            // closes. ZXing's own stop() sometimes leaves the tracks live.
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => {
                    try { t.stop(); } catch (_e) { /* ignore */ }
                });
                streamRef.current = null;
            }
            if (attachedVideo) {
                try { attachedVideo.srcObject = null; } catch (_e) { /* ignore */ }
            }
        };
    }, [open, onScan]);

    if (!open) return null;

    const submitManual = (e) => {
        e.preventDefault();
        const v = manualValue.trim();
        if (!v) return;
        onScan?.(v);
        setManualValue("");
    };

    return (
        <div
            data-testid="barcode-scanner-modal"
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-label={t("scanner.barcodeScanner", "Barcode scanner")}
        >
            <div className="bg-white rounded-lg max-w-md w-full overflow-hidden shadow-2xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
                    <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
                        <Camera className="h-4 w-4" /> {t("scanner.scanBarcode", "Scan barcode")}
                    </div>
                    <button
                        data-testid="barcode-scanner-close"
                        onClick={onClose}
                        className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-neutral-100"
                        aria-label={t("scanner.closeScanner", "Close scanner")}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {status !== "manual" && (
                    <div className="relative bg-black aspect-video">
                        <video
                            ref={videoRef}
                            className="w-full h-full object-cover"
                            data-testid="barcode-scanner-video"
                            muted
                            playsInline
                        />
                        {status === "starting" && (
                            <div className="absolute inset-0 flex items-center justify-center text-white text-xs gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" /> {t("scanner.requestingCamera", "Requesting camera…")}
                            </div>
                        )}
                        {status === "scanning" && (
                            <div className="absolute bottom-2 left-2 right-2 text-center text-[11px] text-white/80">
                                {t("scanner.pointTheCameraAtA", "Point the camera at a barcode")}
                            </div>
                        )}
                        {status === "paused" && (
                            <div className="absolute inset-0 flex items-center justify-center text-white text-xs gap-2 bg-black/60">
                                {t("scanner.barcodeDetectedConfirmOnThe", "Barcode detected — confirm on the product card.")}
                            </div>
                        )}
                    </div>
                )}

                {warnNote && (
                    <div data-testid="barcode-scanner-warn"
                        className="px-4 py-2 bg-amber-50 border-t border-amber-200 text-[11px] text-amber-800">
                        {warnNote}
                    </div>
                )}

                {status !== "manual" && (
                    <div className="px-4 py-2 border-t border-neutral-200 bg-neutral-50 text-[11px] text-neutral-600 flex items-start gap-2"
                         data-testid="barcode-scanner-tip">
                        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                            {t("scanner.holdSteady", "Hold the barcode flat and steady. If scanning a can or curved package, rotate slowly or enter the barcode manually.")}
                        </span>
                    </div>
                )}

                <form
                    onSubmit={submitManual}
                    className="px-4 py-3 border-t border-neutral-200 bg-neutral-50"
                >
                    {status === "manual" && errorMsg && (
                        <p className="text-xs text-amber-700 mb-2" data-testid="barcode-scanner-fallback-reason">
                            {errorMsg}. Enter the barcode manually below.
                        </p>
                    )}
                    {status !== "manual" && (
                        <p className="text-[11px] text-neutral-500 mb-2">
                            {t("scanner.cameraScanningYouCanAlso", "Camera scanning. You can also type the barcode if scanning fails.")}
                        </p>
                    )}
                    <div className="flex items-center gap-2">
                        <input
                            data-testid="barcode-scanner-manual-input"
                            value={manualValue}
                            onChange={(e) => setManualValue(e.target.value)}
                            placeholder={t("scanner.enterBarcode", "Enter barcode")}
                            className="flex-1 h-9 px-3 rounded-md border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
                            autoFocus={status === "manual"}
                        />
                        <button
                            type="submit"
                            data-testid="barcode-scanner-manual-submit"
                            className="h-9 px-3 rounded-md bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-800 disabled:opacity-50"
                            disabled={!manualValue.trim()}
                        >
                            {t("scanner.useCode", "Use code")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
